import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for focus timer logic.
 * Manages countdown, pause/resume, reset, and session tracking.
 */
export function useFocusTimer(defaultMinutes = 25) {
  const [isRunning, setIsRunning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(defaultMinutes * 60);
  const [totalTime, setTotalTime] = useState(defaultMinutes * 60);
  const [sessions, setSessions] = useState(0);
  const intervalRef = useRef(null);

  const reset = useCallback(() => {
    clearInterval(intervalRef.current);
    setIsRunning(false);
    setTimeLeft(totalTime);
  }, [totalTime]);

  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft((prev) => Math.max(0, prev - 1));
      }, 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [isRunning, timeLeft]);

  // "Timer reached zero" side effects live in a committed effect so the
  // countdown updater stays pure (PERF-2).
  useEffect(() => {
    if (isRunning && timeLeft === 0) {
      clearInterval(intervalRef.current);
      setIsRunning(false);
      setSessions((s) => s + 1);
    }
  }, [isRunning, timeLeft]);

  const progress = 1 - timeLeft / totalTime;
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  const toggle = useCallback(() => {
    if (timeLeft === 0) {
      setTimeLeft(totalTime);
      setIsRunning(true);
    } else {
      setIsRunning((p) => !p);
    }
  }, [timeLeft, totalTime]);

  const selectDuration = useCallback((mins) => {
    clearInterval(intervalRef.current);
    setIsRunning(false);
    setTotalTime(mins * 60);
    setTimeLeft(mins * 60);
  }, []);

  return {
    isRunning, timeLeft, totalTime, sessions,
    progress, minutes, seconds,
    toggle, reset, selectDuration,
  };
}
